import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import { Platform, Image } from 'react-native'
import { useAppDispatch, useAppSelector } from '../state/hooks'
import { setDataState } from '../state/Slices/dataSlice'
import { setSettingsState, showAlert } from '../state/Slices/settingsSlice'
import { setPersistedState } from '../state/Slices/persistedSlice'
import { setCheckoutState } from '../state/Slices/checkoutSlice'
import { validateItems } from '../firebase/fbDataRetrieval/validateItems'
import { getOrgData, mergeCheckoutConfig } from '../firebase/fbDataRetrieval/getOrgData'
import { getPromoImageData } from '../firebase/fbDataRetrieval/getPromoImageData'
import { getThirdPartyPOSTypes } from '../firebase/fbDataRetrieval/getThirdPartyPOSTypes'
import { getLoyaltyRewardData } from '../firebase/fbDataRetrieval/getLoyaltyRewardData'
import { getPickupPoints } from '../firebase/fbDataRetrieval/getPickupPoints'
import { getCuisines } from '../firebase/fbDataRetrieval/getCuisines'
import { getOrgCuisinesData } from '../firebase/fbDataRetrieval/getOrgCuisineData'
import { preLoadImage } from '../components/Image/ImageObject'
import { isRestaurantOpen } from '../helpers/restaurantHours'
import { auth } from '../firebase/config'
import RNRestart from 'react-native-restart'
import Colors from '../constants/Colors'
import { DEFAULT_CHECKOUT_CONFIG } from '../state/Slices/settingsSlice'
import useAuthOrders from '../firebase/fbDataRetrieval/useAuthOrders'
import useRestaurantListeners from '../firebase/fbDataRetrieval/ListenerHooks/useRestaurantsListener'
import useUserListener from '../firebase/fbDataRetrieval/ListenerHooks/useUserListener'
import { useFirebaseAnalyticsContext } from './FirebaseAnalyticsContext'
import { addItemToCart, removeItemFromCart } from '../state/Slices/persistedSlice'
import { checkSnooze } from '../helpers/checkSnooze'
import { getCombinedModPrice } from '../screens/Item/Helpers/functions'

interface RetrieveDataContextType {
	// State from RetrieveData
	spinner: boolean
	loading: boolean
	appleAppId: string
	stripePublishableKeyLocalState: string
	menuItemObject: any
	modifierObject: any
	restaurantObject: any

	// Functions
	triggerValidateCartItems: () => Promise<boolean>
	triggerCheckItemAvailability: (data: any) => boolean
}

const RetrieveDataContext = createContext<RetrieveDataContextType | undefined>(undefined)

export const RetrieveDataProvider = ({
	children,
	referralCode,
	loadingTracker,
}: {
	children: React.ReactNode
	referralCode?: string
	loadingTracker?: (loading: boolean) => void
}) => {
	const dispatch = useAppDispatch()
	const { loadedData, allDataLoaded, stripePublishableKey } = useAppSelector(state => state.data)
	const { cart, isTestMode } = useAppSelector(state => state.persisted)
	const { isOrderProcessing } = useAppSelector(state => state.checkout)
	const { allowOrdersOutsideOrgHours, localTimezone } = useAppSelector(state => state.settings)

	const _ = require('lodash')

	// State from RetrieveData
	const [stripePublishableKeyLocalState, setStripePublishableKey] = useState('')
	const [spinner, setSpinner] = useState(true)
	const [loading, setLoading] = useState(true)
	const [appleAppId, setAppleAppId] = useState('')
	const [menuItemObject, setMenuItemObject] = useState(null)
	const [modifierObject, setModifierObject] = useState(null)
	const [restaurantObject, setRestaurantObject] = useState(null)
	const [cartValidationInProgress, setCartValidationInProgress] = useState(false)

	const ref = useRef(null)
	global.payRef = ref

	// Use the same hooks as RetrieveData
	useAuthOrders()
	useUserListener()

	// checkItemAvailability function
	const triggerCheckItemAvailability = useCallback(
		(data: any) => {
			return isRestaurantOpen(
				data,
				loadedData[global.org]?.hours ?? null,
				allowOrdersOutsideOrgHours,
				localTimezone
			)
		},
		[loadedData, allowOrdersOutsideOrgHours, localTimezone]
	)

	// validateModItems helper function
	function validateModItems(modList, modifierItemObject) {
		let update = false
		let priceAlert = false
		let modAlert = false
		if (modList !== undefined && modList.length > 0) {
			const updatedModList = modList.map(list => {
				const updatedOptions = list.options
					.map(mod => {
						const dataItem = modifierItemObject['mod_item_' + mod.id]
						if (
							dataItem &&
							dataItem.id === mod.id &&
							!checkSnooze(dataItem.isSnoozed, dataItem.snoozeUntil)
						) {
							const updatedMod = { ...mod }
							if (mod.price !== dataItem.amount) {
								updatedMod.price = dataItem.amount
								update = true
								priceAlert = true
							}
							if (mod.name !== dataItem.name) {
								updatedMod.name = dataItem.name
								update = true
							}
							return updatedMod
						} else if (mod.isSelected === true) {
							console.log('DELETING MOD ITEM DUE TO DATA MISMATCH', mod.id)
							modAlert = true
							update = true
							const updatedMod = { ...mod }
							updatedMod.isSelected = false
							return updatedMod
						} else {
							return null
						}
					})
					.filter(Boolean) // Filter out null items (mods that were deleted)

				// Return a new list object with updated options
				return {
					...list,
					options: updatedOptions,
				}
			})

			return {
				list: updatedModList || [],
				isUpdate: update,
				isAlert: priceAlert,
				isModAlert: modAlert,
			}
		}
		return { list: modList || [], isUpdate: update, isAlert: priceAlert, isModAlert: modAlert }
	}

	// validateItems function (moved inside provider)
	function validateCartItems(
		menuItemObject,
		modifierItemObject,
		restaurantObject,
		dispatch,
		cart,
		isOrderProcessing,
		checkItemAvailability
	) {
		let alertUpdate: boolean
		let modDeleteAlert: boolean
		let removedUpdate: boolean

		const keys = Object.keys(menuItemObject)

		let removedItemsNumber = 0

		if (isOrderProcessing || cart.length === 0) return

		console.log('VALIDATING CART')

		cart.forEach(item => {
			if (!keys.includes(item.id)) {
				console.log('ITEM NOT IN OBJECT', item.id)
				removedItemsNumber++
				dispatch(removeItemFromCart(item.id, item.idempotencyKey))
			} else {
				const dataItem = menuItemObject[item.id]
				const restaurantItem = restaurantObject[item.rId]
				const itemAvailable = checkItemAvailability(dataItem)

				const itemCheck =
					dataItem &&
					dataItem.restaurantId === item.rId &&
					!checkSnooze(dataItem.isSnoozed, dataItem.snoozeUntil) &&
					itemAvailable &&
					dataItem.id === item.id &&
					!dataItem.isDeleted

				const restaurantCheck =
					restaurantItem && !checkSnooze(restaurantItem.isSnoozed, restaurantItem.snoozeUntil)

				if (itemCheck && restaurantCheck) {
					const updatedItem = { ...item } // Spread item into a new object
					let update = false

					if (dataItem.price !== item.amount) {
						alertUpdate = true
						update = true
						updatedItem.amount = dataItem.price // Update new object
					}
					if (dataItem.promo !== item.promoPrice) {
						alertUpdate = true
						update = true
						updatedItem.promoPrice = dataItem.promo
					}
					if (dataItem.name !== item.name) {
						update = true
						updatedItem.name = dataItem.name
					}

					// Validate modifiers and create a new modList if needed
					const modCheck = validateModItems(item.modList, modifierItemObject)
					updatedItem.modList = modCheck.list

					if (modCheck.isUpdate === true) {
						update = true
					}
					if (modCheck.isAlert === true) {
						alertUpdate = true
					}
					if (modCheck.isModAlert === true) {
						modDeleteAlert = true
					}

					if (update === true) {
						dispatch(removeItemFromCart(item.id, item.idempotencyKey))
						dispatch(setCheckoutState({ isConfirmModalVisible: false }))

						const modPrice = getCombinedModPrice(updatedItem.modList)
						updatedItem.modPriceSum = modPrice
						dispatch(addItemToCart(updatedItem))
					}
				} else {
					removedItemsNumber++
					removedUpdate = true
					console.log('DELETING DUE TO DATA MISMATCH')
					dispatch(removeItemFromCart(item.id, item.idempotencyKey))
					dispatch(setCheckoutState({ isConfirmModalVisible: false }))
				}
			}
		})

		if (removedUpdate === true && cart.length === removedItemsNumber) {
			dispatch(
				showAlert({
					title: 'Items in your cart have been removed',
					body: 'These items are no longer available.',
				})
			)
			return false
		} else if (removedUpdate && cart.length > removedItemsNumber) {
			dispatch(
				showAlert({
					title: 'Some items in your cart have been removed',
					body: 'These items are no longer available. Please review the items in your cart and try again.',
				})
			)
			return false
		} else if (alertUpdate === true && !modDeleteAlert) {
			dispatch(
				showAlert({
					title: 'Prices have changed for items in your cart',
					body: 'Please review your cart before checking out.',
				})
			)
			return false
		} else if (modDeleteAlert === true && !alertUpdate) {
			dispatch(
				showAlert({
					title: 'Modifier selections have changed for items in your cart',
					body: 'Please review your cart before checking out.',
				})
			)
			return false
		} else if (modDeleteAlert === true && alertUpdate === true) {
			dispatch(
				showAlert({
					title: 'Items in your cart have been changed',
					body: 'Please review your cart before checking out.',
				})
			)
			return false
		}
		return true
	}

	// triggerValidateItems function that can be called from children
	const triggerValidateCartItems = useCallback(async () => {
		if (menuItemObject && modifierObject && restaurantObject) {
			const isValid = validateCartItems(
				menuItemObject,
				modifierObject,
				restaurantObject,
				dispatch,
				cart,
				isOrderProcessing,
				triggerCheckItemAvailability
			)

			return isValid
		}
		return false
	}, [
		menuItemObject,
		modifierObject,
		restaurantObject,
		dispatch,
		cart,
		isOrderProcessing,
		triggerCheckItemAvailability,
		setCartValidationInProgress,
	])

	useRestaurantListeners(setSpinner, setRestaurantObject, setMenuItemObject, setModifierObject)

	// Main data fetching effect (same as RetrieveData)
	useEffect(() => {
		const fetchData = async () => {
			try {
				const orgObject = {}
				console.log('LOADING DATA ' + new Date().toLocaleString())

				const [
					orgData,
					promoImageObject,
					thirdPartyPOSTypes,
					loyaltyRewardObject,
					pickupPoints,
					cuisineObject,
					orgCuisineObject,
				] = await Promise.all([
					getOrgData(),
					getPromoImageData(),
					getThirdPartyPOSTypes(),
					getLoyaltyRewardData(),
					getPickupPoints(),
					getCuisines(),
					getOrgCuisinesData(),
				])
				console.log('ORGANIZATION DATA LOADED ' + new Date().toLocaleString())

				const data: any = orgData
				const restaurantIds = data.restaurants

				setAppleAppId(data.iosAppId || '')
				global.dineIn = data.dineInEnabled || false
				orgObject[global.org] = data

				// Handle Stripe keys
				if (data.stripe?.publishableKey) {
					setStripePublishableKey(
						data.environment === 'production'
							? data.stripe.publishableKey.production
							: data.stripe.publishableKey.sandbox
					)
				}

				// Merge all fetched data together
				const updatedData = _.merge(
					{},
					orgObject,
					loadedData,
					promoImageObject,
					thirdPartyPOSTypes,
					loyaltyRewardObject,
					pickupPoints,
					cuisineObject,
					orgCuisineObject
				)

				// Handle pickup points logic
				if (data.pickupPointsEnabled && updatedData['pickupPoints']?.length === 1) {
					dispatch(setPersistedState({ selectedPickupPoint: updatedData['pickupPoints'][0] }))
				}

				// Set organization color and logo
				let orgLogo = data.logo || ''
				if (global.kiosk && data.kioskLogo) {
					orgLogo = data.kioskLogo
				}
				global.orgColor = global.kiosk && data.kioskColor ? data.kioskColor : data.color
				global.orgColor2 = data.textColor || '#848484'

				// Set the document style on web
				if (Platform.OS === 'web') {
					document.documentElement.style.setProperty(
						'--safe-area-color',
						data?.appDisplayOptions?.backgroundColor || '#f8f8f8'
					)
				}

				// Dispatch settings state
				dispatch(
					setSettingsState({
						delivery: {
							freeDeliveryMultiplier: data.delivery?.freeDeliveryMultiplier || -1,
							isSandbox: data.delivery?.isSandbox || data.environment === 'sandbox',
							orgCoverage: data.delivery?.organizationCoverage?.amount || 0,
							orgCoverageType: data.delivery?.organizationCoverage?.type || '',
							actionIfUndeliverable:
								data.delivery?.actionIfUndeliverable === 'dispose' ? 'dispose' : 'return_to_pickup',
							pickupInstructions: data.delivery?.pickupInstructions || '',
							minSpend: data.delivery?.minimumOrder || 0,
							freeDeliveryMinimumOrder: data.delivery?.freeDeliveryMinimumOrder || null,
							freeDeliveryDistanceThreshold: data.delivery?.freeDeliveryDistanceThreshold || null,
						},
						deliveryOrgReceivedPercent: data.delivery?.organizationCoverage?.coveragePercent ?? 100,
						serviceFee: data.serviceFee ?? 0,
						serviceFeeGST: data.serviceFeeGST ?? 0,
						serviceFeePST: data.serviceFeePST ?? 0,
						serviceFeeTax: data.serviceFeeTax ?? 0,
						scheduleDaysAhead: data.scheduleDaysAhead ?? 14,
						scheduleTimeInterval: data.scheduleTimeInterval ?? 15,
						localTimezone: data.timezone ?? 'America/Vancouver',
						allowOrdersOutsideOrgHours:
							data.allowOrdersOutsideOrgHours === 'single' ||
							data.allowOrdersOutsideOrgHours === 'multi'
								? true
								: false,
						outsideOrgHoursOrderType: data.allowOrdersOutsideOrgHours,
						pickupPointsEnabled: data.pickupPointsEnabled || false,
						isSingleBrand: data.singleBrandEnabled || false,
						posLoyaltyEnabled: data.posLoyaltyEnabled || false,
						dineInEnabled: data.dineInEnabled || false,
						usesTableNumbers: data.usesTableNumbers || false,
						posProvider: data.posProvider || '',
						paymentProvider: data.paymentProvider || 'stripe',
						isSandbox: data.environment === 'sandbox',
						webOrdersEnabled: data.webOrdersEnabled ?? true,
						deliveryEnabled:
							data.delivery?.enabled && !global.kiosk ? data.delivery?.enabled : false,
						checkoutConfig: mergeCheckoutConfig(DEFAULT_CHECKOUT_CONFIG, data.checkoutConfig ?? {}),
						enableGiftCard: data.enableGiftCard ?? false,
						privacyPolicy: {
							emailAddress: data?.privacyPolicy?.emailAddress,
						},
						appDisplayOptions: {
							homeLayout: data?.appDisplayOptions?.homeLayout || 'categories',
							showRestaurants: data?.appDisplayOptions?.showRestaurants || false,
							showCategories: data?.appDisplayOptions?.showCategories || false,
							showBrands: data?.appDisplayOptions?.showBrands || false,
							showLoyalty: data?.appDisplayOptions?.showLoyalty || false,
							showCuisines: data?.appDisplayOptions?.showCuisines || false,
							showDisclaimerText: data?.appDisplayOptions?.showDisclaimerText || false,
							randomizeRestaurantDisplay:
								data?.appDisplayOptions?.randomizeRestaurantDisplay ?? false,
							showTermsOfUse: data?.appDisplayOptions?.showTermsOfUse ?? false,
							banner: {
								mobile: data?.appDisplayOptions?.banner?.mobile || null,
								desktop: data?.appDisplayOptions?.banner?.desktop || null,
								backgroundColor: data?.appDisplayOptions?.banner?.backgroundColor || null,
							},
							kiosk: {
								showPromoImages:
									data?.appDisplayOptions?.kiosk?.showPromoImages === false ? false : true,
								startScreenImage: data?.appDisplayOptions?.kiosk?.startScreenImage || null,
							},
							showTipRestaurant: data?.appDisplayOptions?.showTipRestaurant ?? false,
							styles: {
								backgroundColor: data?.appDisplayOptions?.styles?.backgroundColor || '#f8f8f8',
								buttonAlternateColor:
									data?.appDisplayOptions?.styles?.buttonAlternateColor || Colors.greyscale[2],
								hasBoxShadow: data?.appDisplayOptions?.styles?.hasBoxShadow ?? true,
							},
							receipt: {
								customText: data?.appDisplayOptions?.receipt?.customText || null,
							},
						},
						address: data?.address,
						email: data?.email,
						customTipOptions: {
							delivery: data?.customTipOptions?.delivery,
							pickup: data?.customTipOptions?.pickup,
							kiosk: data?.customTipOptions?.kiosk,
						},
						country: data?.country?.toUpperCase() || 'CA',
						currency: data?.currency || 'cad',
						seo: {
							metaTitle: data?.seo?.metaTitle,
							metaDescription: data?.seo?.metaDescription,
						},
					})
				)

				global.ignorePrepTimeAtClose = data.ignorePrepTimeAtClose ?? false

				// Dispatch data state
				dispatch(
					setDataState({
						loadedData: updatedData,
						webAppURL: data.webURL || '',
						iosAppId: data.iosAppId || '',
						organizationLogo: orgLogo,
						organizationLogoDark: data.logoDark || '',
						emailLogo: data.emailLogo || orgLogo || '',
						stripePublishableKey:
							stripePublishableKeyLocalState !== '' && !isTestMode
								? stripePublishableKeyLocalState
								: data.environment === 'sandbox' || isTestMode
									? data.stripe?.publishableKey?.sandbox
									: data.stripe?.publishableKey?.production,
						allDataLoaded: true,
						restaurantIds: restaurantIds,
					})
				)

				// Preload images
				if (Platform.OS !== 'web') {
					if (data.organizationHeader) preLoadImage(data.organizationHeader)
					if (data.logo) preLoadImage(data.logo)
					if (data.logoDark) preLoadImage(data.logoDark)
					if (data.kioskOrganizationLogo) preLoadImage(data.kioskOrganizationLogo)
				}
			} catch (error) {
				console.error(error)
				if (global.kiosk) {
					dispatch(
						showAlert({
							title: 'Error loading data!',
							body: '',
							colorA: 'black',
							colorB: Colors.greyscale[5],
							buttonA: 'Logout',
							buttonB: 'Refresh',
							onPressB: () => {
								RNRestart.Restart()
							},
							onPressA: () => {
								auth.signOut()
								RNRestart.Restart()
							},
						})
					)
				} else {
					dispatch(
						showAlert({
							title: 'Error loading data!',
							buttonA: 'Refresh',
							colorA: global.orgColor ?? 'black',
							onPressA: () => {
								RNRestart.Restart()
							},
						})
					)
				}
			}
		}

		fetchData()
	}, [])

	// Validation effect (same as RetrieveData)
	useEffect(() => {
		const validateData = async () => {
			if (!spinner) {
				if (loadingTracker !== null) loadingTracker(true)
			}
			if (!spinner && allDataLoaded) {
				// Ensure the data is available before calling validateItems
				if (menuItemObject && modifierObject && restaurantObject) {
					await triggerValidateCartItems()
				}
				setLoading(false)
			}
		}

		validateData()
	}, [
		spinner,
		allDataLoaded,
		menuItemObject,
		modifierObject,
		restaurantObject,
		triggerCheckItemAvailability,
		triggerValidateCartItems,
		loadingTracker,
	])

	const value = {
		// State
		spinner,
		loading,
		appleAppId,
		stripePublishableKeyLocalState,
		menuItemObject,
		modifierObject,
		restaurantObject,
		cartValidationInProgress,

		// Functions
		triggerValidateCartItems,
		triggerCheckItemAvailability,
	}

	return <RetrieveDataContext.Provider value={value}>{children}</RetrieveDataContext.Provider>
}

export const useRetrieveDataContext = () => {
	const context = useContext(RetrieveDataContext)
	if (context === undefined) {
		throw new Error('useRetrieveData must be used within a RetrieveDataProvider')
	}
	return context
}
